import { useEffect, useState } from 'react'
import Tooltip from '@mui/material/Tooltip'
import { IHuddlePeer } from '@common/interfaces/huddles'
import { PhotoSubjectType } from '@common/interfaces/photo'
import cn from 'classnames'
import { LOCAL_LEVEL_KEY, SPEAKING_LEVEL } from '../../helpers/huddle/huddleAudio'
import Icon from '../Common/Icon'
import ProfilePhoto from '../Common/ProfilePhoto'
import styles from './Huddle.module.scss'

/** How often the talking rings are checked; quicker reads as flicker. */
const LEVEL_POLL_MS = 150

interface IProps {
  peers: IHuddlePeer[]
  selfPeerId: string | null
  connectedPeerIds: string[]
  getLevels: () => Map<string, number>
}

const speakingIn = (levels: Map<string, number>, peers: IHuddlePeer[], selfPeerId: string | null) =>
  peers
    .filter(({ peerId, isMuted }) => {
      if (isMuted) return false
      const key = peerId === selfPeerId ? LOCAL_LEVEL_KEY : peerId
      return (levels.get(key) ?? 0) >= SPEAKING_LEVEL
    })
    .map(({ peerId }) => peerId)
    .sort()
    .join(',')

/** Everyone in the room, with a ring around whoever is talking. */
const HuddlePeers = ({ peers, selfPeerId, connectedPeerIds, getLevels }: IProps) => {
  const [speaking, setSpeaking] = useState('')
  useEffect(() => {
    const timer = setInterval(() => {
      setSpeaking(speakingIn(getLevels(), peers, selfPeerId))
    }, LEVEL_POLL_MS)
    return () => clearInterval(timer)
  }, [getLevels, peers, selfPeerId])

  const speakingIds = new Set(speaking.split(','))
  return (
    <div className={styles.peers}>
      {peers.map((peer) => {
        const isSelf = peer.peerId === selfPeerId
        const isConnecting = !isSelf && !connectedPeerIds.includes(peer.peerId)
        const label = `${isSelf ? `${peer.name} (you)` : peer.name}${isConnecting ? ' · connecting' : ''}${peer.isMuted ? ' · muted' : ''}`
        return (
          <Tooltip key={peer.peerId} title={label}>
            <span
              className={cn(styles.peer, {
                [styles.peerSpeaking]: speakingIds.has(peer.peerId),
                [styles.peerConnecting]: isConnecting,
              })}
              aria-label={label}
            >
              <ProfilePhoto
                name={peer.name}
                subjectType={PhotoSubjectType.Users}
                id={peer.userId}
                size="small"
              />
              {peer.isMuted && (
                <span className={styles.peerMuted}>
                  <Icon name="mic-off" size="xs" />
                </span>
              )}
            </span>
          </Tooltip>
        )
      })}
    </div>
  )
}

export default HuddlePeers
